<script setup lang="ts">
import type { FriendLink, FriendLinkRSSItem, FriendLinkStatus, LinkApplyInput, PageResult, RandomFriendLink } from '~/types/blog'
import { normalizeFieldBool, normalizeFieldNumber, normalizeFieldText } from '~/composables/useCustomFields'
import { resolveImageAssetUrl } from '~/composables/useAssetUrl'

const route = useRoute()
const blogApi = useBlogApi()
const siteData = await useSiteData()
const siteBaseUrl = resolveSiteBaseUrl(siteData?.setting?.customFields)

const fullLoadPageSize = 100
const rssPageSize = 5
const initialRssPages = 2

// linkAddedYear 提取友情链接的添加年份
const linkAddedYear = (item: FriendLink) => item.CreatedAt?.slice(0, 4)

type LinkGroupKey = 'normal' | 'abnormal' | 'unknown'
type LinkGroupState = {
  title: string
  serviceStatus: 'up' | 'down' | 'unknown'
  list: FriendLink[]
  total: number
  page: number
  loading: boolean
  loadingMore: boolean
  loaded: boolean
}
type LinkListSnapshot = {
  list: FriendLink[]
  total: number
  page: number
  loaded: boolean
}
type LinkInitialPayload = {
  classifyByServiceStatus: boolean
  loadMode: 'all' | 'more'
  list: LinkListSnapshot
  groups: Record<LinkGroupKey, LinkListSnapshot>
}

const classifyByServiceStatus = computed(() =>
  normalizeFieldBool(siteData?.setting?.customFields, 'linksClassifyByServiceStatus', false)
)
const linkLoadMode = computed(() => {
  const mode = normalizeFieldText(siteData?.setting?.customFields, 'linksLoadMode', 'more').toLowerCase()
  return mode === 'all' ? 'all' : 'more'
})
const isLoadAllMode = computed(() => linkLoadMode.value === 'all')
const linkPageSize = computed(() => {
  if (isLoadAllMode.value) {
    return 15
  }
  const rawSize = Math.floor(normalizeFieldNumber(siteData?.setting?.customFields, 'linksLoadMorePageSize', 15))
  return Math.min(50, Math.max(1, rawSize))
})
const showRandomFriendLinkButton = computed(() =>
  normalizeFieldBool(siteData?.setting?.customFields, 'showRandomFriendLinkButton', true)
)
const showLinksRssCard = computed(() =>
  normalizeFieldBool(siteData?.setting?.customFields, 'showLinksRssCard', false)
)

const linkState = reactive({
  list: [] as FriendLink[],
  total: 0,
  page: 0,
  loading: false,
  loadingMore: false,
  loaded: false
})

const linkGroupStates = reactive<Record<LinkGroupKey, LinkGroupState>>({
  normal: {
    title: '正常链接',
    serviceStatus: 'up',
    list: [],
    total: 0,
    page: 0,
    loading: false,
    loadingMore: false,
    loaded: false
  },
  abnormal: {
    title: '异常链接',
    serviceStatus: 'down',
    list: [],
    total: 0,
    page: 0,
    loading: false,
    loadingMore: false,
    loaded: false
  },
  unknown: {
    title: '未检测',
    serviceStatus: 'unknown',
    list: [],
    total: 0,
    page: 0,
    loading: false,
    loadingMore: false,
    loaded: false
  }
})

const linkGroupOrder: LinkGroupKey[] = ['normal', 'abnormal', 'unknown']
const linkGroups = computed(() => linkGroupOrder.map(key => ({ key, state: linkGroupStates[key] })))
const { data: fieldDefs } = await useAsyncData('link:fields', () => blogApi.getFieldDefList('link'))
const { data: captcha, refresh: refreshCaptcha } = await useAsyncData('link:captcha', () => blogApi.getCaptcha())

const siteName = normalizeFieldText(siteData?.setting?.customFields, 'siteName')
const pageDesc = normalizeFieldText(siteData?.setting?.customFields, 'linksPageDescription', '欢迎交换链接，提交后将进入审核流程。')
const linkApplyIntro = normalizeFieldText(
  siteData?.setting?.customFields,
  'linkApplyIntro',
  '欢迎交换链接。请确保站点可正常访问，且已添加本站链接后再提交申请。'
)

useSeoMeta({
  title: `友情链接 - ${siteName}`,
  description: pageDesc,
  ogTitle: `友情链接 - ${siteName}`,
  ogDescription: pageDesc
})

useHead({
  link: [{ rel: 'canonical', href: `${siteBaseUrl}${route.path}` }]
})

const form = reactive({
  name: '',
  url: '',
  rss: '',
  logo: '',
  email: '',
  description: '',
  remark: '',
  captcha: '',
  customFields: {} as Record<string, unknown>
})

const state = reactive({
  submitting: false,
  error: '',
  success: ''
})
const applyNotice = reactive({
  show: false,
  type: 'success' as 'success' | 'error',
  text: ''
})
const statusState = reactive({
  loading: false,
  error: '',
  success: ''
})
const statusQuery = reactive({
  email: '',
  url: ''
})
const statusList = ref<FriendLinkStatus[]>([])
const rssState = reactive({
  list: [] as FriendLinkRSSItem[],
  total: 0,
  page: 0,
  pageSize: rssPageSize,
  loadingMore: false
})
const modal = reactive({
  applyOpen: false,
  statusOpen: false,
  randomOpen: false
})
const randomLinkLoading = ref(false)
const randomLinkPreview = ref<RandomFriendLink | null>(null)
const randomLinkCountdownMs = ref(0)
const randomRedirectDelayMs = 3000
const randomProgressRadius = 26
const randomProgressCircumference = 2 * Math.PI * randomProgressRadius
let applyNoticeTimer: ReturnType<typeof setTimeout> | null = null
let randomRedirectTimer: ReturnType<typeof setTimeout> | null = null
let randomCountdownTimer: ReturnType<typeof setInterval> | null = null
let randomCountdownStartedAt = 0

const hasMoreLinks = computed(() => linkState.list.length < linkState.total)
const hasMoreRss = computed(() => rssState.list.length < rssState.total)
const linkSkeletonRows = computed(() => Math.max(6, linkPageSize.value))
const hasMoreLinksByGroup = (key: LinkGroupKey) => linkGroupStates[key].list.length < linkGroupStates[key].total
const showLinkGroupTitle = (key: LinkGroupKey) => key !== 'normal'
const randomCountdownProgress = computed(() => {
  if (randomRedirectDelayMs <= 0) {
    return 0
  }
  return Math.max(0, Math.min(1, randomLinkCountdownMs.value / randomRedirectDelayMs))
})
const randomCountdownOffset = computed(() =>
  randomProgressCircumference * (1 - randomCountdownProgress.value)
)
const randomCountdownSecond = computed(() => Math.max(0, Math.ceil(randomLinkCountdownMs.value / 1000)))
const visibleLinkGroups = computed(() =>
  linkGroups.value.filter(group =>
    group.state.list.length > 0 ||
    group.state.loading ||
    (group.state.loadingMore && group.state.list.length > 0)
  )
)
const hasAnyGroupedLinks = computed(() =>
  linkGroupOrder.some(key => linkGroupStates[key].list.length > 0)
)
const createEmptySnapshot = (): LinkListSnapshot => ({
  list: [],
  total: 0,
  page: 0,
  loaded: false
})

const fetchLinksSnapshot = async (loadAll: boolean, serviceStatus?: 'up' | 'down' | 'unknown'): Promise<LinkListSnapshot> => {
  if (!loadAll) {
    const data = await blogApi.getFriendLinkList({ page: 1, pageSize: linkPageSize.value, serviceStatus })
    const list = Array.isArray(data?.list) ? data.list : []
    return {
      list,
      total: Number(data?.total || 0),
      page: 1,
      loaded: true
    }
  }

  const mergedList: FriendLink[] = []
  let total = 0
  let page = 0
  for (let nextPage = 1; ; nextPage += 1) {
    const data = await blogApi.getFriendLinkList({ page: nextPage, pageSize: fullLoadPageSize, serviceStatus })
    const pageList = Array.isArray(data?.list) ? data.list : []
    mergedList.push(...pageList)
    total = Number(data?.total || 0)
    page = nextPage
    if (pageList.length === 0 || mergedList.length >= total) {
      break
    }
  }
  return {
    list: mergedList,
    total,
    page,
    loaded: true
  }
}

const applyLinkSnapshot = (snapshot: LinkListSnapshot) => {
  linkState.list = snapshot.list
  linkState.total = snapshot.total
  linkState.page = snapshot.page
  linkState.loaded = snapshot.loaded
  linkState.loading = false
  linkState.loadingMore = false
}

const applyGroupSnapshot = (key: LinkGroupKey, snapshot: LinkListSnapshot) => {
  const group = linkGroupStates[key]
  group.list = snapshot.list
  group.total = snapshot.total
  group.page = snapshot.page
  group.loaded = snapshot.loaded
  group.loading = false
  group.loadingMore = false
}

const pushApplyNotice = (type: 'success' | 'error', text: string) => {
  applyNotice.type = type
  applyNotice.text = text
  applyNotice.show = true
  if (applyNoticeTimer) {
    clearTimeout(applyNoticeTimer)
  }
  applyNoticeTimer = setTimeout(() => {
    applyNotice.show = false
  }, 5000)
}

onBeforeUnmount(() => {
  if (applyNoticeTimer) {
    clearTimeout(applyNoticeTimer)
  }
  if (randomRedirectTimer) {
    clearTimeout(randomRedirectTimer)
  }
  if (randomCountdownTimer) {
    clearInterval(randomCountdownTimer)
  }
})

const updateField = ({ key, value }: { key: string; value: unknown }) => {
  form.customFields = {
    ...form.customFields,
    [key]: {
      value,
      remark: ''
    }
  }
}

const reloadCaptcha = async () => {
  await refreshCaptcha()
  form.captcha = ''
}

const loadLinks = async (append = false) => {
  if (append) {
    if (linkState.loading || linkState.loadingMore || !hasMoreLinks.value) {
      return
    }
    linkState.loadingMore = true
  } else {
    linkState.loading = true
  }
  const nextPage = append ? linkState.page + 1 : 1
  try {
    const data = await blogApi.getFriendLinkList({ page: nextPage, pageSize: linkPageSize.value })
    const pageList = Array.isArray(data?.list) ? data.list : []
    linkState.list = append ? [...linkState.list, ...pageList] : pageList
    linkState.total = Number(data?.total || 0)
    linkState.page = nextPage
    linkState.loaded = true
  } finally {
    linkState.loading = false
    linkState.loadingMore = false
  }
}

const loadLinksAll = async () => {
  linkState.loading = true
  linkState.loadingMore = false
  linkState.page = 0
  linkState.total = 0
  linkState.list = []
  try {
    let nextPage = 1
    for (;;) {
      const data = await blogApi.getFriendLinkList({ page: nextPage, pageSize: fullLoadPageSize })
      const pageList = Array.isArray(data?.list) ? data.list : []
      linkState.list = [...linkState.list, ...pageList]
      linkState.total = Number(data?.total || 0)
      linkState.page = nextPage
      if (pageList.length === 0 || linkState.list.length >= linkState.total) {
        break
      }
      nextPage += 1
    }
    linkState.loaded = true
  } finally {
    linkState.loading = false
  }
}

const loadLinksByGroup = async (key: LinkGroupKey, append = false) => {
  const group = linkGroupStates[key]
  if (append) {
    if (group.loading || group.loadingMore || !hasMoreLinksByGroup(key)) {
      return
    }
    group.loadingMore = true
  } else {
    group.loading = true
  }
  const nextPage = append ? group.page + 1 : 1
  try {
    const data = await blogApi.getFriendLinkList({
      page: nextPage,
      pageSize: linkPageSize.value,
      serviceStatus: group.serviceStatus
    })
    const pageList = Array.isArray(data?.list) ? data.list : []
    group.list = append ? [...group.list, ...pageList] : pageList
    group.total = Number(data?.total || 0)
    group.page = nextPage
    group.loaded = true
  } finally {
    group.loading = false
    group.loadingMore = false
  }
}

const loadLinksByGroupAll = async (key: LinkGroupKey) => {
  const group = linkGroupStates[key]
  group.loading = true
  group.loadingMore = false
  group.page = 0
  group.total = 0
  group.list = []
  try {
    let nextPage = 1
    for (;;) {
      const data = await blogApi.getFriendLinkList({
        page: nextPage,
        pageSize: fullLoadPageSize,
        serviceStatus: group.serviceStatus
      })
      const pageList = Array.isArray(data?.list) ? data.list : []
      group.list = [...group.list, ...pageList]
      group.total = Number(data?.total || 0)
      group.page = nextPage
      if (pageList.length === 0 || group.list.length >= group.total) {
        break
      }
      nextPage += 1
    }
    group.loaded = true
  } finally {
    group.loading = false
  }
}

const loadGroupedLinks = async () => {
  await Promise.all(linkGroupOrder.map(key => loadLinksByGroup(key)))
}

const loadGroupedLinksAll = async () => {
  await Promise.all(linkGroupOrder.map(key => loadLinksByGroupAll(key)))
}

const reloadAllLinks = async () => {
  if (classifyByServiceStatus.value) {
    if (isLoadAllMode.value) {
      await loadGroupedLinksAll()
      return
    }
    await loadGroupedLinks()
    return
  }
  if (isLoadAllMode.value) {
    await loadLinksAll()
    return
  }
  await loadLinks()
}

const loadMoreRss = async () => {
  if (!showLinksRssCard.value || rssState.loadingMore || !hasMoreRss.value) {
    return
  }
  rssState.loadingMore = true
  try {
    const nextPage = rssState.page + 1
    const data = await blogApi.getFriendLinkRSSList({
      page: nextPage,
      pageSize: rssState.pageSize
    })
    const pageList = Array.isArray(data?.list) ? data.list : []
    rssState.list = [...rssState.list, ...pageList]
    rssState.total = Number(data?.total || 0)
    rssState.page = Number(data?.page || nextPage)
    rssState.pageSize = Number(data?.pageSize || rssState.pageSize || rssPageSize)
  } finally {
    rssState.loadingMore = false
  }
}

const initialLinkDataKey = `link:list:${classifyByServiceStatus.value ? 'group' : 'flat'}:${isLoadAllMode.value ? 'all' : 'more'}:${linkPageSize.value}`
const { data: initialLinkPayload } = await useAsyncData<LinkInitialPayload>(initialLinkDataKey, async () => {
  const payload: LinkInitialPayload = {
    classifyByServiceStatus: classifyByServiceStatus.value,
    loadMode: isLoadAllMode.value ? 'all' : 'more',
    list: createEmptySnapshot(),
    groups: {
      normal: createEmptySnapshot(),
      abnormal: createEmptySnapshot(),
      unknown: createEmptySnapshot()
    }
  }
  if (payload.classifyByServiceStatus) {
    const groupSnapshots = await Promise.all(
      linkGroupOrder.map(key => fetchLinksSnapshot(payload.loadMode === 'all', linkGroupStates[key].serviceStatus))
    )
    linkGroupOrder.forEach((key, index) => {
      payload.groups[key] = groupSnapshots[index]
    })
    return payload
  }
  payload.list = await fetchLinksSnapshot(payload.loadMode === 'all')
  return payload
})

const initialPayload = initialLinkPayload.value
if (initialPayload) {
  if (initialPayload.classifyByServiceStatus) {
    linkGroupOrder.forEach(key => {
      applyGroupSnapshot(key, initialPayload.groups[key] || createEmptySnapshot())
    })
    applyLinkSnapshot(createEmptySnapshot())
  } else {
    applyLinkSnapshot(initialPayload.list)
    linkGroupOrder.forEach(key => {
      applyGroupSnapshot(key, createEmptySnapshot())
    })
  }
}

const initialRssDataKey = `link:rss:${showLinksRssCard.value ? 'on' : 'off'}:${rssPageSize}:${initialRssPages}`
const { data: initialRSSList } = await useAsyncData<PageResult<FriendLinkRSSItem>>(initialRssDataKey, async () => {
  if (!showLinksRssCard.value) {
    return {
      list: [],
      total: 0,
      page: 1,
      pageSize: rssPageSize
    }
  }
  const pageRequests = Array.from({ length: initialRssPages }, (_, index) =>
    blogApi.getFriendLinkRSSList({
      page: index + 1,
      pageSize: rssPageSize
    })
  )
  const pages = await Promise.all(pageRequests)
  const mergedList = pages.flatMap(page => Array.isArray(page?.list) ? page.list : [])
  const lastPage = pages[pages.length - 1]
  return {
    list: mergedList,
    total: Number(pages[0]?.total || 0),
    page: Number(lastPage?.page || pages.length || 1),
    pageSize: rssPageSize
  }
})
rssState.list = Array.isArray(initialRSSList.value?.list) ? initialRSSList.value.list : []
rssState.total = Number(initialRSSList.value?.total || 0)
rssState.page = Number(initialRSSList.value?.page || 1)
rssState.pageSize = Number(initialRSSList.value?.pageSize || rssPageSize)

const submit = async () => {
  state.error = ''
  state.success = ''

  const payload: LinkApplyInput = {
    name: form.name.trim(),
    url: form.url.trim(),
    rss: form.rss.trim(),
    logo: form.logo.trim(),
    email: form.email.trim(),
    description: form.description.trim(),
    remark: form.remark.trim(),
    captcha: form.captcha.trim(),
    captchaId: captcha.value?.captchaId || '',
    customFields: form.customFields
  }

  if (!payload.name || !payload.url || !payload.email || !payload.captcha) {
    state.error = '请完整填写必填项'
    return
  }

  try {
    state.submitting = true
    await blogApi.applyFriendLink(payload)
    state.success = '申请已提交，等待审核。'
    pushApplyNotice('success', '申请提交成功，等待审核。')
    form.name = ''
    form.url = ''
    form.rss = ''
    form.logo = ''
    form.email = ''
    form.description = ''
    form.remark = ''
    form.captcha = ''
    form.customFields = {}
    modal.applyOpen = false
    await Promise.all([reloadAllLinks(), refreshCaptcha()])
  } catch (error) {
    state.error = (error as Error).message || '提交失败'
    pushApplyNotice('error', state.error || '提交失败')
    await refreshCaptcha()
  } finally {
    state.submitting = false
  }
}

const statusLabel = (status?: string) => {
  if (status === 'pending') {
    return '待审核'
  }
  if (status === 'approved') {
    return '已通过'
  }
  if (status === 'rejected') {
    return '已拒绝'
  }
  return '未知'
}

const statusClass = (status?: string) => {
  if (status === 'approved') return 'status-ok'
  if (status === 'rejected') return 'status-bad'
  if (status === 'pending') return 'status-warn'
  return ''
}

const linkServiceDotClass = (status?: string) => {
  if (status === 'up') return 'dot-up'
  if (status === 'down') return 'dot-down'
  if (status === 'unknown') return 'dot-unknown'
  return ''
}

const queryStatus = async () => {
  statusState.error = ''
  statusState.success = ''
  statusList.value = []

  const email = statusQuery.email.trim()
  const url = statusQuery.url.trim()
  if (!email && !url) {
    statusState.success = ''
    statusState.error = '请至少填写邮箱或站点地址'
    return
  }

  try {
    statusState.loading = true
    const data = await blogApi.getFriendLinkStatus({ email, url, page: 1, pageSize: 200 })
    statusList.value = data?.list || []
    statusState.success = (data?.total || 0) > 0 ? `查询成功，共 ${data.total} 条记录` : '未找到对应友链申请'
  } catch (error) {
    statusState.error = (error as Error).message || '查询失败'
  } finally {
    statusState.loading = false
  }
}

const stopRandomRedirectCountdown = () => {
  if (randomRedirectTimer) {
    clearTimeout(randomRedirectTimer)
    randomRedirectTimer = null
  }
  if (randomCountdownTimer) {
    clearInterval(randomCountdownTimer)
    randomCountdownTimer = null
  }
}

const closeRandomPreview = () => {
  stopRandomRedirectCountdown()
  modal.randomOpen = false
  randomLinkPreview.value = null
  randomLinkCountdownMs.value = 0
}

const jumpToRandomFriendLink = () => {
  const url = String(randomLinkPreview.value?.url || '').trim()
  if (!url) {
    statusState.error = '随机链接无效，无法跳转'
    closeRandomPreview()
    return
  }
  closeRandomPreview()
  if (import.meta.client) {
    window.location.href = url
  }
}

const startRandomRedirectCountdown = () => {
  stopRandomRedirectCountdown()
  randomLinkCountdownMs.value = randomRedirectDelayMs
  randomCountdownStartedAt = Date.now()
  randomCountdownTimer = setInterval(() => {
    const elapsed = Date.now() - randomCountdownStartedAt
    randomLinkCountdownMs.value = Math.max(0, randomRedirectDelayMs - elapsed)
    if (randomLinkCountdownMs.value <= 0 && randomCountdownTimer) {
      clearInterval(randomCountdownTimer)
      randomCountdownTimer = null
    }
  }, 32)
  randomRedirectTimer = setTimeout(() => {
    jumpToRandomFriendLink()
  }, randomRedirectDelayMs)
}

const visitRandomFriendLink = async () => {
  if (randomLinkLoading.value) {
    return
  }
  statusState.error = ''
  randomLinkLoading.value = true
  try {
    const data = await blogApi.getRandomFriendLink()
    const url = String(data?.url || '').trim()
    if (!url) {
      pushApplyNotice('error', '暂无可访问的友情链接')
      return
    }
    randomLinkPreview.value = data
    modal.randomOpen = true
    startRandomRedirectCountdown()
  } catch (error) {
    const message = (error as Error).message || '随机访问失败'
    statusState.error = message
    pushApplyNotice('error', message)
  } finally {
    randomLinkLoading.value = false
  }
}
</script>

<template>
  <section class="links-page">
    <div class="card header-card">
      <h1>友情链接</h1>
      <p>{{ pageDesc }}</p>
      <p class="apply-intro">{{ linkApplyIntro }}</p>
      <div class="action-row header-actions">
        <button class="submit-btn apply-btn" type="button" @click="modal.applyOpen = true">
          <i class="fa-solid fa-paper-plane" aria-hidden="true" />
          发起申请
        </button>
        <button class="submit-btn" type="button" @click="modal.statusOpen = true">查询审核状态</button>
        <button v-if="showRandomFriendLinkButton" class="submit-btn" type="button" :disabled="randomLinkLoading" @click="visitRandomFriendLink">
          <i class="fa-solid fa-shuffle" aria-hidden="true" />
          {{ randomLinkLoading ? '跳转中...' : '随机访问' }}
        </button>
      </div>
      <Transition name="fade-slide">
        <p v-if="applyNotice.show" class="apply-notice" :class="`is-${applyNotice.type}`">{{ applyNotice.text }}</p>
      </Transition>
    </div>

    <div class="card list-card">
      <template v-if="classifyByServiceStatus">
        <div v-for="group in visibleLinkGroups" :key="group.key" class="link-group">
          <h3 v-if="showLinkGroupTitle(group.key)" class="link-group-title">{{ group.state.title }}</h3>
          <BlogFriendLinkSkeleton v-if="group.state.loading && !group.state.loaded" :row-count="linkSkeletonRows" />
          <template v-else-if="group.state.list.length">
            <div class="link-list">
              <a
                v-for="item in group.state.list"
                :key="`${group.key}-${item.ID}`"
                :href="item.url"
                target="_blank"
                rel="noopener noreferrer"
                :class="['link-item', linkServiceDotClass(item.serviceStatus)]"
              >
                <span v-if="linkAddedYear(item)" class="link-year" aria-hidden="true">{{ linkAddedYear(item) }}</span>
                <BlogImage v-if="item.logo" :src="resolveImageAssetUrl(item.logo)" :alt="item.name" />
                <div>
                  <div class="link-head">
                    <BlogUiTooltip :content="item.name">
                      <strong class="link-title">
                        <span>{{ item.name }}</span>
                      </strong>
                    </BlogUiTooltip>
                  </div>
                  <BlogUiTooltip :content="item.description || item.url">
                    <p>{{ item.description || item.url }}</p>
                  </BlogUiTooltip>
                </div>
              </a>
            </div>
            <BlogFriendLinkSkeleton v-if="group.state.loadingMore" :row-count="linkSkeletonRows" />
            <div v-if="!isLoadAllMode && hasMoreLinksByGroup(group.key)" class="load-more-wrap">
              <button class="submit-btn" type="button" :disabled="group.state.loadingMore" @click="loadLinksByGroup(group.key, true)">
                {{ group.state.loadingMore ? '加载中...' : '加载更多' }}
              </button>
            </div>
          </template>
        </div>
        <div v-if="!visibleLinkGroups.length && !hasAnyGroupedLinks" class="empty-card">暂无友情链接</div>
      </template>
      <BlogFriendLinkSkeleton v-else-if="linkState.loading && !linkState.loaded" :row-count="linkSkeletonRows" />
      <template v-else-if="linkState.list.length">
        <div class="link-list">
          <a
            v-for="item in linkState.list"
            :key="item.ID"
            :href="item.url"
            target="_blank"
            rel="noopener noreferrer"
            :class="['link-item', linkServiceDotClass(item.serviceStatus)]"
          >
            <span v-if="linkAddedYear(item)" class="link-year" aria-hidden="true">{{ linkAddedYear(item) }}</span>
            <BlogImage v-if="item.logo" :src="resolveImageAssetUrl(item.logo)" :alt="item.name" />
            <div>
              <div class="link-head">
                <BlogUiTooltip :content="item.name">
                  <strong class="link-title">
                    <span>{{ item.name }}</span>
                  </strong>
                </BlogUiTooltip>
              </div>
              <BlogUiTooltip :content="item.description || item.url">
                <p>{{ item.description || item.url }}</p>
              </BlogUiTooltip>
            </div>
          </a>
        </div>
        <BlogFriendLinkSkeleton v-if="linkState.loadingMore" :row-count="linkSkeletonRows" />
        <div v-if="!isLoadAllMode && hasMoreLinks" class="load-more-wrap">
          <button class="submit-btn" type="button" :disabled="linkState.loadingMore" @click="loadLinks(true)">
            {{ linkState.loadingMore ? '加载中...' : '加载更多' }}
          </button>
        </div>
      </template>
      <div v-else class="empty-card">暂无友情链接</div>
    </div>

    <BlogLinksRssSection
      v-if="showLinksRssCard"
      :items="rssState.list"
      :total="rssState.total"
      :has-more="hasMoreRss"
      :loading-more="rssState.loadingMore"
      @load-more="loadMoreRss"
    />

    <div v-if="modal.applyOpen" class="modal-mask" @click.self="modal.applyOpen = false">
      <form class="card modal-card form-card" @submit.prevent="submit">
        <button class="modal-close" type="button" aria-label="关闭" @click="modal.applyOpen = false">
          <i class="fa-solid fa-xmark" aria-hidden="true" />
        </button>
        <h2>友情链接申请</h2>

        <label>
          <span class="required">站点名称</span>
          <input v-model="form.name" type="text" />
        </label>
        <label>
          <span class="required">站点地址（http/https）</span>
          <input v-model="form.url" type="url" />
        </label>
        <label>
          <span>RSS 地址</span>
          <input v-model="form.rss" type="url" />
        </label>
        <label>
          <span>站点 Logo</span>
          <input v-model="form.logo" type="url" />
        </label>
        <label>
          <span class="required">联系邮箱</span>
          <input v-model="form.email" type="email" />
        </label>
        <label>
          <span>站点描述</span>
          <BlogResizableTextarea>
            <textarea v-model="form.description" rows="2" />
          </BlogResizableTextarea>
        </label>
        <label>
          <span>备注</span>
          <BlogResizableTextarea>
            <textarea v-model="form.remark" rows="2" />
          </BlogResizableTextarea>
        </label>

        <BlogFieldInput
          v-for="item in fieldDefs || []"
          :key="item.ID || item.key"
          :def="item"
          :fields="form.customFields"
          @update="updateField"
        />

        <label>
          <span class="required">验证码</span>
          <div class="captcha-row">
            <input v-model="form.captcha" type="text" :maxlength="captcha?.captchaLength || 6" />
            <button class="refresh-btn" type="button" @click="reloadCaptcha">
              <BlogImage
                v-if="captcha?.picPath"
                class="captcha-img"
                :src="captcha.picPath"
                alt="captcha"
                loading="eager"
                :progressive="false"
              />
            </button>
          </div>
        </label>

        <p v-if="state.error" class="error">{{ state.error }}</p>
        <p v-if="state.success" class="success">{{ state.success }}</p>

        <div class="dialog-actions">
          <button class="submit-btn apply-btn" type="submit" :disabled="state.submitting">
            <i class="fa-solid fa-paper-plane" aria-hidden="true" />
            {{ state.submitting ? '提交中' : '提交申请' }}
          </button>
        </div>
      </form>
    </div>

    <div v-if="modal.statusOpen" class="modal-mask" @click.self="modal.statusOpen = false">
      <div class="card modal-card status-card">
        <button class="modal-close" type="button" aria-label="关闭" @click="modal.statusOpen = false">
          <i class="fa-solid fa-xmark" aria-hidden="true" />
        </button>
        <h2>查询审核状态</h2>
        <div class="status-grid">
          <label>
            <span>邮箱</span>
            <input v-model="statusQuery.email" type="email" />
          </label>
          <label>
            <span>站点地址</span>
            <input v-model="statusQuery.url" type="url" />
          </label>
        </div>
        <div class="dialog-actions status-query-actions">
          <button class="submit-btn apply-btn" type="button" :disabled="statusState.loading" @click="queryStatus()">
            <i class="fa-solid fa-magnifying-glass" aria-hidden="true" />
            {{ statusState.loading ? '查询中' : '查询状态' }}
          </button>
          <span
            v-if="statusState.error || statusState.success"
            class="status-query-feedback"
            :class="statusState.error ? 'error' : 'success'"
          >
            {{ statusState.error || statusState.success }}
          </span>
        </div>
        <div v-if="statusList.length" class="status-result-wrap">
          <div class="status-result-list">
            <div
              v-for="(item, index) in statusList"
              :key="`${item.url || 'url'}-${item.lastUpdated || 'time'}-${index}`"
              class="status-result"
            >
              <div class="status-result-row">
                <i class="fa-solid fa-link" aria-hidden="true" />
                  <span>
                    <BlogUiTooltip :content="item.url">
                      <strong>站点：</strong>{{ item.name || '-' }}
                    </BlogUiTooltip>
                  </span>
              </div>
              <div class="status-result-row" :class="statusClass(item.status)">
                <i :class="item.status === 'approved' ? 'fa-solid fa-check' : item.status === 'rejected' ? 'fa-solid fa-xmark' : 'fa-solid fa-hourglass'" aria-hidden="true" />
                <span><strong>状态：</strong>{{ statusLabel(item.status) }}</span>
              </div>
              <div class="status-result-row">
                <i class="fa-solid fa-at" aria-hidden="true" />
                <BlogUiTooltip :content="item.remark">
                  <span><strong>备注：</strong>{{ item.remark || '-' }}</span>
                </BlogUiTooltip>
              </div>
              <div class="status-result-row">
                <i class="fa-solid fa-clock-rotate-left" aria-hidden="true" />
                <span><strong>更新时间：</strong>{{ item.lastUpdated || '-' }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="modal.randomOpen && randomLinkPreview" class="modal-mask" @click.self="closeRandomPreview">
      <div class="card modal-card random-card">
        <button class="modal-close" type="button" aria-label="关闭" @click="closeRandomPreview">
          <i class="fa-solid fa-xmark" aria-hidden="true" />
        </button>
        <h2>随机访问中</h2>
        <p class="random-card-intro">3 秒后自动跳转，你也可以取消。</p>
        <div class="random-link-meta">
          <BlogImage
            v-if="randomLinkPreview.logo"
            class="random-link-logo"
            :src="resolveImageAssetUrl(randomLinkPreview.logo)"
            :alt="randomLinkPreview.name"
          />
          <div class="random-link-text">
            <strong>{{ randomLinkPreview.name || '未命名站点' }}</strong>
            <BlogUiTooltip :content="randomLinkPreview.description ? randomLinkPreview.description : randomLinkPreview.url">
            <span>{{ randomLinkPreview.description ? randomLinkPreview.description : randomLinkPreview.url }}</span>
            </BlogUiTooltip>
          </div>
          <div class="random-countdown-wrap" aria-live="polite">
            <svg class="countdown-ring" viewBox="0 0 64 64" role="img" aria-label="倒计时">
              <circle class="countdown-ring-track" cx="32" cy="32" :r="randomProgressRadius" />
              <circle
                class="countdown-ring-progress"
                cx="32"
                cy="32"
                :r="randomProgressRadius"
                :stroke-dasharray="randomProgressCircumference"
                :stroke-dashoffset="randomCountdownOffset"
              />
            </svg>
            <div class="countdown-value">{{ randomCountdownSecond }}s</div>
          </div>
        </div>
        <div class="dialog-actions random-actions">
          <button class="submit-btn" type="button" @click="closeRandomPreview">取消跳转</button>
          <button class="submit-btn apply-btn" type="button" @click="jumpToRandomFriendLink">立即前往</button>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.links-page {
  display: grid;
  gap: 14px;
}

.header-card {
  padding: 20px;
}

h1 {
  font-size: var(--heading-title-size);
}

h2 {
  font-size: 24px;
}

.header-card p {
  margin-top: 8px;
  color: var(--text-soft);
}

.apply-intro {
  font-size: 13px;
}

.list-card,
.form-card {
  display: grid;
  gap: 12px;
  align-content: flex-start;
  padding: 16px;
}

.action-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.action-row .submit-btn,
.action-row .refresh-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.header-actions {
  margin-top: 12px;
}

.link-list {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.link-group {
  display: grid;
  gap: 10px;
}

.link-group + .link-group {
  margin-top: 2px;
}

.link-group-title {
  margin: 0;
  font-size: 14px;
  color: var(--text-soft);
}

.load-more-wrap {
  display: flex;
  justify-content: center;
  margin-top: 6px;
}

.status-grid {
  display: grid;
  gap: 8px;
  margin-bottom: 10px;
}

.status-result {
  padding: 10px;
  border: 0;
  border-radius: 10px;
  background: var(--bg);
  min-width: 0;
}

.status-result-wrap {
  margin-top: 8px;
  display: grid;
  gap: 8px;
}

.status-result-list {
  min-width: 0;
  max-height: min(38vh, 340px);
  overflow: auto;
  display: grid;
  gap: 8px;
  padding-right: 4px;
}

.status-result-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 4px 0;
}

.status-result-row span {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.status-result p {
  margin: 0;
  display: grid;
  grid-template-columns: 18px minmax(0, 1fr);
  gap: 8px;
  align-items: center;
}

.status-result p + p {
  margin-top: 8px;
}

.status-result i {
  flex-shrink: 0;
  font-style: normal;
  text-align: center;
  color: var(--text-soft);
}

.status-result i::before {
  font-size: 14px;
}

.link-item {
  position: relative;
  display: flex;
  gap: 10px;
  align-items: center;
  padding: 10px;
  border-radius: var(--radius);
  color: var(--text);
  text-decoration: none;
  min-width: 0;
  overflow: hidden;
  background: var(--bg);
  transition:
    transform 0.16s ease,
    background-color 0.16s ease;
}

.link-item::before,
.link-item::after {
  content: none;
  position: absolute;
  top: 11px;
  right: 11px;
  border-radius: 999px;
  pointer-events: none;
}

.link-year {
  position: absolute;
  right: 6px;
  bottom: -6px;
  color: transparent;
  -webkit-text-stroke: 1px color-mix(in srgb, var(--text) 6%, transparent);
  font-family: var(--font-number-outline);
  font-size: 48px;
  font-weight: 800;
  line-height: 1;
  letter-spacing: -2px;
  pointer-events: none;
  user-select: none;
  transition: color 0.16s ease;
}

.link-item:hover .link-year {
  color: color-mix(in srgb, var(--text) 6%, transparent);
  -webkit-text-stroke-color: transparent;
}

.link-item > img,
.link-item > div {
  position: relative;
}

.link-item.dot-up::after,
.link-item.dot-down::after,
.link-item.dot-unknown::after {
  content: '';
  width: 8px;
  height: 8px;
}

.link-item.dot-up::before,
.link-item.dot-down::before,
.link-item.dot-unknown::before {
  content: '';
  width: 8px;
  height: 8px;
  opacity: 0.4;
  animation: service-dot-breathe 2.4s ease-out infinite;
}

.link-item:hover {
  transform: translateY(-1px);
  background: color-mix(in srgb, var(--brand) 4%, var(--bg));
}

.link-item img {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  object-fit: cover;
  flex-shrink: 0;
}

.link-item p {
  color: var(--text-soft);
  font-size: 13px;
  margin-top: 4px;
  line-height: 1.55;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.link-item > div {
  min-width: 0;
}

.link-item strong {
  font-weight: 600;
  display: block;
  line-height: 1.45;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.link-title {
  display: block;
  min-width: 0;
}

.link-head {
  min-width: 0;
}

.link-head :deep(.ui-tooltip-wrap) {
  display: block;
  min-width: 0;
}

.link-item.dot-up::after {
  background: #41b480;
}

.link-item.dot-up::before {
  background: #41b480;
}

.link-item.dot-down::after {
  background: #f95555;
}

.link-item.dot-down::before {
  background: #f95555;
}

.link-item.dot-unknown::after {
  background: #bcc5d2;
}

.link-item.dot-unknown::before {
  background: #bcc5d2;
}

@keyframes service-dot-breathe {
  0% {
    transform: scale(1);
    opacity: 0.42;
  }
  70% {
    transform: scale(2.1);
    opacity: 0;
  }
  100% {
    transform: scale(2.1);
    opacity: 0;
  }
}

label {
  display: grid;
  gap: 6px;
}

label span {
  color: var(--text-soft);
  font-size: 13px;
}

label span.required::after {
  content: '*';
  margin-left: 4px;
  color: #d64545;
}

input,
textarea {
  width: 100%;
  border: 0;
  border-radius: 10px;
  padding: 10px;
  color: var(--text);
  background: var(--editor-bg);
  transition: box-shadow 0.18s ease;
}

input:focus,
textarea:focus {
  outline: none;
  box-shadow: var(--editor-focus-shadow);
}

textarea {
  min-height: 55px;
}

.captcha-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px;
}

.refresh-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 36px;
  border: 0;
  border-radius: 10px;
  padding: 0;
  color: var(--text);
  cursor: pointer;
  overflow: hidden;
  background: var(--editor-bg);
}

.captcha-img {
  height: 36px;
  max-width: 240px;
}

.error {
  color: #d64545;
  font-size: 13px;
}

.success {
  color: #1f8a50;
  font-size: 13px;
}

.apply-notice {
  margin: 0;
  margin-top: -2px;
  border-radius: 10px;
  padding: 9px 11px;
  font-size: 13px;
  line-height: 1.5;
}

.apply-notice.is-success {
  color: #1f8a50;
  background: color-mix(in srgb, #1f8a50 10%, transparent);
  border: 1px solid color-mix(in srgb, #1f8a50 24%, var(--line));
}

.apply-notice.is-error {
  color: #c03636;
  background: color-mix(in srgb, #c03636 10%, transparent);
  border: 1px solid color-mix(in srgb, #c03636 24%, var(--line));
}

.submit-btn {
  width: fit-content;
  border: 0;
  border-radius: 999px;
  padding: 9px 16px;
  cursor: pointer;
  background: var(--bg);
  color: var(--text);
  transition:
    color 0.18s ease,
    background-color 0.18s ease;
}

.submit-btn:hover:not(:disabled) {
  color: var(--brand);
  background: var(--brand-soft);
}

.submit-btn.apply-btn {
  color: #fff;
  background: var(--brand);
}

.submit-btn.apply-btn:hover:not(:disabled) {
  color: #fff;
  background: color-mix(in srgb, var(--brand) 88%, #000 12%);
}

.submit-btn:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

.modal-mask {
  --dropdown-z-index: 70;
  position: fixed;
  inset: 0;
  z-index: 60;
  display: grid;
  place-items: center;
  padding: 16px;
  background: rgba(0, 0, 0, 0.36);
  backdrop-filter: blur(2px);
}

.modal-card {
  position: relative;
  width: min(680px, 100%);
  max-height: calc(100vh - 48px);
  overflow: auto;
  padding: 16px;
  border: 0;
  box-shadow: var(--modal-shadow);
  animation: modal-pop 180ms ease;
}

.form-card {
  --dropdown-border: 0;
  --dropdown-focus-shadow: var(--editor-focus-shadow);
}

.form-card :deep(.field-input input:not([type='checkbox'])),
.form-card :deep(.field-input textarea) {
  border: 0;
  background: var(--editor-bg);
  transition: box-shadow 0.18s ease;
}

.form-card :deep(.field-input input:not([type='checkbox']):focus),
.form-card :deep(.field-input textarea:focus) {
  outline: none;
  box-shadow: var(--editor-focus-shadow);
}

.modal-close {
  position: absolute;
  top: 10px;
  right: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 0;
  border-radius: 999px;
  color: var(--text-soft);
  cursor: pointer;
  background: var(--bg);
  transition:
    color 0.18s ease,
    background-color 0.18s ease;
}

.modal-close:hover {
  color: var(--brand);
  background: var(--brand-soft);
}

.modal-close i {
  font-size: 15px;
  line-height: 1;
}

.modal-card h2 {
  margin-bottom: 10px;
}

.status-card {
  width: min(520px, 100%);
}

.random-card {
  width: min(430px, 100%);
  display: grid;
  gap: 10px;
}

.random-card h2 {
  margin: 0;
  font-size: 22px;
}

.random-card-intro {
  margin: 0;
  color: var(--text-soft);
  font-size: 12px;
}

.random-link-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px;
  border-radius: 10px;
  border: 0;
  background: var(--bg);
}

.random-link-logo {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  object-fit: cover;
}

.random-link-text {
  flex: 1;
  min-width: 0;
  display: grid;
  gap: 4px;
}

.random-link-text strong,
.random-link-text span {
  margin: 0;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.random-link-text p {
  margin: 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.random-link-text span,
.random-link-text p {
  color: var(--text-soft);
  font-size: 13px;
}

.random-countdown-wrap {
  position: relative;
  width: 58px;
  height: 58px;
  flex-shrink: 0;
  margin-left: auto;
}

.countdown-ring {
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
}

.countdown-ring-track,
.countdown-ring-progress {
  fill: none;
  stroke-width: 6;
}

.countdown-ring-track {
  stroke: color-mix(in srgb, var(--line) 88%, transparent);
}

.countdown-ring-progress {
  stroke: var(--brand);
  stroke-linecap: round;
  transition: stroke-dashoffset 32ms linear;
}

.countdown-value {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  font-weight: 600;
  color: var(--text);
}

.random-actions {
  margin-top: 2px;
  justify-content: flex-end;
}

@keyframes modal-pop {
  from {
    opacity: 0;
    transform: translateY(8px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: opacity 160ms ease, transform 180ms ease;
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.dialog-actions {
  display: flex;
  justify-content: flex-start;
  gap: 8px;
  margin-top: 8px;
}

.status-query-actions {
  display: grid;
  grid-template-columns: max-content 1fr;
  align-items: center;
}

.status-query-feedback {
  justify-self: end;
  white-space: nowrap;
}

.status-ok {
  color: #1f8a50;
}

.status-bad {
  color: #c03636;
}

.status-warn {
  color: #b8821a;
}

@media (max-width: 900px) {
  .modal-card {
    width: 100%;
  }

  .link-list {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 760px) {
  .link-list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 520px) {
  .link-list {
    grid-template-columns: 1fr;
  }
}
</style>
